import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject } from 'rxjs';

// Define all possible channels
export enum IpcChannel {
  UPDATE_AVAILABLE = 'update-available',
  GET_VERSION = 'get-version',
  DOWNLOAD_IF_UPDATE_AVAILABLE = 'download-if-update-available',
  RESTART_APP = 'restart-app',
  GET_LOGS = 'get-logs',
  CLEAR_LOGS = 'clear-logs',
  DOWNLOAD_LOGS = 'download-logs',
  SAVE_USER = 'save-user',
  GET_USER = 'get-user',
  OPEN_URL = 'open-url',
  LIVE_LOG_ENTRY = 'live-log-entry',
}

/** OLD
 * type message = "getVersion" 
  | "startServer" | "checkDBConnection" | "installUpdate" | "checkForUpdates" | "getUser" 
  | "saveUser" | "getLogs" | "isItUpToDate" | "getProgress" | "openUrl" | "clearLogs" | "downloadLogs";
 */

// Define message interfaces
export interface IpcMessage<T = any> {
  type: string;
  payload?: T;
}

export interface IpcResponse {
  data: any;
  error?: Error;
}

export interface LogEntry {
  level: 'info' | 'error' | 'warn' | 'debug' | 'testing' | 'log';
  message: string;
  timestamp: string;
  icon: string;
}

// Window interface
interface ElectronWindow extends Window {
  electron: {
    send: (channel: string, data: any) => void;
    on: (channel: string, callback: (data: any) => void) => void;
    removeListener: (channel: string) => void;
  };
}

@Injectable({
  providedIn: 'root',
})
export class IpcV2Service implements OnDestroy {
  private readonly window: ElectronWindow;
  private readonly listeners: Map<string, Subject<any>> = new Map();

  constructor() {
    this.window = window as unknown as ElectronWindow;

    // Auto-start listening for live logs
    if (this.window.electron) {
      this.on<LogEntry>(IpcChannel.LIVE_LOG_ENTRY).subscribe({
        next: () => {
          // Auto-logging happens in the 'on' method above
        },
        error: (error) => {
          console.error('Live log error:', error);
        },
      });
      console.log('🚀 Auto-listening for live logs started');
    }
  }

  isElectron(): boolean {
    // Check for our specific electron bridge
    return !!(this.window?.electron);
  }
  send(channel: IpcChannel, data: IpcMessage = null): void {
    if (!this.window.electron) {
      console.error('Electron IPC not available');
      return;
    }
    this.window.electron?.send(channel, data);
  }

  on<T>(channel: IpcChannel): Observable<T> {
    const channelKey = channel.toString(); // Add this line - channelKey was undefined!

    if (!this.listeners.has(channelKey)) {
      const subject = new Subject<T>();
      this.listeners.set(channelKey, subject);

      this.window.electron?.on(channelKey, (data: T) => {
        // Auto-console.log for live log entries (simplified condition)
        if (channel === IpcChannel.LIVE_LOG_ENTRY) {
          this.autoLogEntry(data as unknown as LogEntry);
        }

        subject.next(data);
      });
    }
    return this.listeners.get(channelKey).asObservable();
  }

  sendAndReceive<T>(channel: IpcChannel, data?: any): Observable<IpcResponse> {
    return new Observable<IpcResponse>((observer) => {
      if (!this.window.electron) {
        observer.error(new Error('Electron IPC not available'));
        return () => {};
      }

      console.log(`🔄 SendAndReceive IPC: ${channel}`, data);

      // Setup listener for multiple responses
      const responseHandler = (response: unknown) => {
        console.log(`📥 SendAndReceive Response: ${channel}`, response);
        if (response != null) {
          observer.next(response as IpcResponse);
          
          // ✅ FIX: Check if this is a final response for certain channels
          if (this.isFinalResponse(channel, response as IpcResponse)) {
            observer.complete();
          }
        } else {
          observer.error(new Error('No response received'));
        }
      };

      this.window.electron.on(channel, responseHandler);
      this.window.electron.send(channel, data);

      return () => {
        this.window.electron.removeListener(channel);
      };
    });
  }
  private isFinalResponse(channel: IpcChannel, response: IpcResponse): boolean {
    // For download progress, only complete when status is final
    if (channel === IpcChannel.DOWNLOAD_IF_UPDATE_AVAILABLE) {
      const status = response.data?.status;
      return status === 'completed' || status === 'error' || status === 'not-available';
    }
    
    // For other channels, complete after first response
    return true;
  }

  removeListener(channel: IpcChannel): void {
    if (this.listeners.has(channel)) {
      this.listeners.get(channel)?.complete();
      this.listeners.delete(channel);
      this.window.electron.removeListener(channel);
    }
  }

  removeAllListeners(): void {
    this.listeners.forEach((_, channel) => {
      this.removeListener(channel as IpcChannel);
    });
  }

  ngOnDestroy(): void {
    this.removeAllListeners();
  }

 private autoLogEntry(logEntry: LogEntry): void {
  if (!logEntry) return;

  const time = new Date(logEntry.timestamp).toLocaleTimeString();
  const LENGTH = 30;
  const shortMessage = logEntry.message.length > 80 ? 
    logEntry.message.substring(0, LENGTH) + '...' : 
    logEntry.message;
  
  const logMessage = `${logEntry.icon} [${time}] ${logEntry.level.toUpperCase()}: ${shortMessage}`;

  // If message is long, make it collapsible
  if (logEntry.message.length > LENGTH) {
    switch (logEntry.level) {
      case 'error':
        console.groupCollapsed(logMessage);
        console.error('>>', logEntry.message);
        console.groupEnd();
        break;
      case 'warn':
        console.groupCollapsed(logMessage);
        console.warn('>>', logEntry.message);
        console.groupEnd();
        break;
      case 'info':
        console.groupCollapsed(logMessage);
        console.info('>>', logEntry.message);
        console.groupEnd();
        break;
      case 'debug':
        console.groupCollapsed(logMessage);
        console.debug('>>', logEntry.message);
        console.groupEnd();
        break;
      default:
        console.groupCollapsed(logMessage);
        console.log('>>', logEntry.message);
        console.groupEnd();
    }
  } else {
    // Short messages display normally
    switch (logEntry.level) {
      case 'error':
        console.error(logMessage);
        break;
      case 'warn':
        console.warn(logMessage);
        break;
      case 'info':
        console.info(logMessage);
        break;
      case 'debug':
        console.debug(logMessage);
        break;
      default:
        console.log(logMessage);
    }
  }
}
}
